// src/views/PremiumConstruction.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import useAuthStore from "../store/useAuthStore";
import useProfileStore from "../store/customer/useProfileStore";

import MobileBottomNav from "../components/common/MobileBottomNav";
import GenericModal from "../components/common/GenericModal";
import { User, Loader2 } from "lucide-react";
import useAddressStore from "../store/useAddressStore";
import SuccessPopup from "../components/common/SuccessPopup";
import { axiosEnquiryInstance } from "../lib/axiosInstance";

const PremiumConstruction = () => {
  const [showMore, setShowMore] = useState(false);
  const navigate = useNavigate();
  const { token, userType, user } = useAuthStore();
  const isCustomerLoggedIn = token && userType === "customer" && user;
  const { profile, fetchProfile } = useProfileStore();

  const [isOpen, setIsOpen] = useState(false);
  const [showFixedNav, setShowFixedNav] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessPopupOpen, setIsSuccessPopupOpen] = useState(false);

  const {
    districts,
    taluks,
    fetchMappedDistricts,
    fetchMappedTaluks,
    isLoadingDistricts,
    isLoadingTaluks,
  } = useAddressStore();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    district: "",
    taluk: "",
    query_message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    fetchMappedDistricts("Tamil Nadu");
  }, [fetchMappedDistricts]);

  useEffect(() => {
    if (form.district) {
      fetchMappedTaluks(form.district, "Tamil Nadu");
    } else {
      useAddressStore.setState({ taluks: [] });
    }
  }, [form.district, fetchMappedTaluks]);

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!form.email.trim()) newErrors.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(form.email))
      newErrors.email = "Please enter a valid email address.";
    if (!form.phone.trim()) newErrors.phone = "Phone number is required";
    else if (!/^\d{10}$/.test(form.phone))
      newErrors.phone = "Phone number must be exactly 10 digits";
    if (!form.district.trim()) newErrors.district = "District is required";
    if (!form.taluk.trim()) newErrors.taluk = "Taluk is required";
    if (!form.query_message.trim()) newErrors.query_message = "Query is required";
    return newErrors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Clear error for this field
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });

    if (name === "phone") {
      const digits = value.replace(/\D/g, "");
      setForm((prev) => ({ ...prev, [name]: digits }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
        ...(name === "district" && { taluk: "" }),
      }));
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      setIsSubmitting(true);
      try {
        const payload = { ...form, category: "Commercial Construction" };
        const response = await axiosEnquiryInstance.post("/enquiries", payload);

        if (response.data.success) {
          setIsSuccessPopupOpen(true);
          setForm({ name: "", email: "", phone: "", district: "", taluk: "", query_message: "" });
          setSubmitAttempted(false);
          setErrors({});
        } else {
          toast.error(response.data.message || "Submission failed. Please try again.");
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "An error occurred while submitting your enquiry.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (isCustomerLoggedIn && !profile) {
      fetchProfile();
    }
  }, [isCustomerLoggedIn, profile, fetchProfile]);

  useEffect(() => {
    if (isCustomerLoggedIn && profile) {
      setForm((prev) => ({
        ...prev,
        name: profile.name || "",
        email: profile.email || "",
        phone: profile.contact_number || "",
        district: profile.district || "",
        taluk: profile.taluk || "",
      }));
    }
  }, [isCustomerLoggedIn, profile]);

  useEffect(() => {
    const handleScroll = () => {
      const heroSection = document.querySelector(".hero-section");
      if (heroSection) {
        setShowFixedNav(heroSection.getBoundingClientRect().bottom <= 0);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLoginRedirect = (selectedUserType) => {
    if (token && userType) {
      if (userType !== selectedUserType) {
        toast.error(`You are already logged in as a ${userType}`);
        return;
      }
      if (userType === "builder") navigate("/builder/dashboard");
      else navigate("/customer/projectinvite");
    } else {
      navigate(`/login?type=${selectedUserType}`);
    }
    setIsOpen(false);
  };

  const handleSignupCardClick = (signupUserType) => {
    setIsSignupModalOpen(false);
    navigate(signupUserType === "builder" ? "/builder/signup" : "/customer/signup");
  };

  const navLinks = [
    { href: "/", text: "HOME" },
    { href: "/#services", text: "SERVICES" },
    { isButton: true, text: "JOIN US", onClick: () => setIsSignupModalOpen(true) },
    // { href: "/job-portal", text: "JOB PORTAL" },
    { href: "/support", text: "SUPPORT" },
  ];

  const renderNavLinks = (isMobile = false, isFixed = false) =>
    navLinks.map((link, index) =>
      link.isButton ? (
        <button key={index} onClick={link.onClick} className={isMobile ? "block w-full text-left px-3 py-2 text-black font-medium hover:bg-gray-100 rounded-md" : `relative px-4 py-2 text-[14px] font-bold transition-all hover:rounded-full duration-300 ${isFixed ? "text-black hover:text-gray-500 hover:bg-gray-100" : "text-white hover:text-gray-300"}`}>
          {link.text}
        </button>
      ) : (
        <a key={index} href={link.href} onClick={(e) => { e.preventDefault(); if (link.href.startsWith("#")) { const element = document.querySelector(link.href); if (element) { const yOffset = element.getBoundingClientRect().top + window.pageYOffset - 100; window.scrollTo({ top: yOffset, behavior: "smooth" }); } } else { navigate(link.href); } if (isMobile) setIsOpen(false); }} className={isMobile ? "block px-3 py-2 text-black font-medium hover:bg-gray-100 rounded-md" : `relative px-4 py-2 text-[14px] font-bold transition-all hover:rounded-full duration-300 ${isFixed ? "text-black hover:text-gray-500 hover:bg-gray-100" : "text-white hover:text-gray-300"}`}>
          {link.text}
        </a>
      )
    );

  const renderCustomerButton = (isMobile = false, isFixed = false) =>
    token && userType === "customer" && user ? (
      <button onClick={() => navigate("/customer/profile")} className={isMobile ? "flex items-center gap-2 w-full text-left px-3 py-2 bg-blue-600 text-white font-medium hover:bg-blue-700 rounded-md" : `flex items-center gap-2 bg-blue-600 text-white font-bold px-4 py-2 rounded-full hover:bg-blue-700`}>
        <User className="w-4 h-4" />
        Hello, {user.name.split(" ")[0]}
      </button>
    ) : (
      <button onClick={() => handleLoginRedirect("customer")} className={isMobile ? "block w-full text-left px-3 py-2 text-black font-medium hover:bg-gray-100 rounded-md" : "bg-blue-600 text-white font-bold px-6 py-2 rounded-full hover:bg-blue-700"} style={!isMobile && !isFixed ? { marginLeft: "60px" } : {}}>
        Customer Login
      </button>
    );

  return (
    <div className="w-full pb-16 lg:pb-0">
      <SuccessPopup
        isOpen={isSuccessPopupOpen}
        onClose={() => setIsSuccessPopupOpen(false)}
        message="Thank you for your enquiry! We will contact you within 24 hours."
      />
      <GenericModal isOpen={isSignupModalOpen} onClose={() => setIsSignupModalOpen(false)} title="Join Ducktail As" size="max-w-lg">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4">
          <div onClick={() => handleSignupCardClick("builder")} className="flex flex-col items-center p-4 border border-gray-300 rounded-lg hover:shadow-lg hover:border-blue-500 transition-all duration-200 cursor-pointer bg-gray-50">
            <img src="/assets/builder.png" alt="Builder" className="w-24 h-24 object-contain mb-3" />
            <h3 className="text-md font-semibold text-gray-700">Builder</h3>
            <p className="text-xs text-gray-500 text-center mt-1">Showcase your projects and connect with clients.</p>
          </div>
          <div onClick={() => handleSignupCardClick("customer")} className="flex flex-col items-center p-4 border border-gray-300 rounded-lg hover:shadow-lg hover:border-blue-500 transition-all duration-200 cursor-pointer bg-gray-50">
            <img src="/assets/young-man.png" alt="Customer" className="w-24 h-24 object-contain mb-3" />
            <h3 className="text-md font-semibold text-gray-700">Customer</h3>
            <p className="text-xs text-gray-500 text-center mt-1">Find verified builders for your dream project.</p>
          </div>
        </div>
      </GenericModal>

      {/* Fixed Navigation */}
      {showFixedNav && (
        <div
          className="fixed top-0 left-0 right-0 bg-white shadow-md"
          style={{ zIndex: 9998 }}
        >
          <div className="max-w-[90rem] mx-auto px-2  sm:px-4 lg:px-6">
            <div className="flex justify-between items-center h-16">
              <div
                className="flex-shrink-0 cursor-pointer"
                onClick={() => navigate("/")}
              >
                <img
                  src="/assets/black logo.png"
                  alt="Ducktail"
                  className="h-12 sm:h-12"
                />
              </div>
              <div className="hidden lg:flex space-x-3 items-center">
                {renderNavLinks(false, true)}
                {renderCustomerButton(false, true)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section with Navigation */}
      <div
        className="hero-section relative w-full flex items-center justify-center"
        style={{
          height: typeof window !== "undefined"
            ? (window.innerWidth < 1024 ? '100vh' : '86vh')
            : '100vh'
        }}
      >
        {/* Navigation Bar */}
        <div className="absolute top-0 left-0 right-0 z-30">
          <div className="bg-transparent">
            <div className="max-w-[90rem] mx-auto px-2 sm:px-4 lg:px-6">
              <div className="flex justify-between items-center h-16">
                <div className="flex-shrink-0 cursor-pointer" onClick={() => navigate("/")}>
                  <img src="/assets/Transparent background.png" alt="Ducktail" className="h-16 sm:h-18 lg:h-20" />
                </div>
                <div className="hidden lg:flex space-x-3 items-center">
                  {renderNavLinks()}
                  {renderCustomerButton()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          {/* Desktop Background Image */}
          <img
            src="/assets/Premium.jpg"
            alt="Premium Construction"
            className="absolute inset-0 w-full h-full object-cover object-center hidden sm:block"
          />
          {/* Mobile Background Image */}
          <img
            src="/assets/commercial mobile.jpg"
            alt="Premium Construction"
            className="absolute inset-0 w-full h-full object-cover object-center block sm:hidden"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        {/* Content Container */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4  pb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">

            {/* Left Content */}
            <div className="text-white space-y-6">
              <div className="space-y-4">
                <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-yellow-400">
                  Commercial Construction
                </h1>
                <p className="text-base md:text-lg text-gray-200 max-w-xl hidden sm:block">
                  Ducktail delivers premium construction services, creating residential, commercial, and sustainable spaces with expert craftsmanship and innovative design excellence.
                </p>
                <p className="text-base md:text-lg text-gray-200 max-w-xl block sm:hidden">
                  Ducktail builds premium, sustainable spaces combining craftsmanship, innovation, and your vision.
                </p>
              </div>
            </div>

            {/* Form Card */}
            <div 
              className="w-full max-w-md mx-auto lg:mx-0 p-5 sm:p-6 lg:p-4 rounded-xl shadow-2xl relative overflow-hidden"
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)'
              }}
            >
              <form onSubmit={handleFormSubmit} className="space-y-3 text-sm relative z-10">
                <style>
                  {`
                    .modern-input {
                      width: 100%;
                      padding: 0.5rem 0.75rem;
                      height: 2.25rem;
                      background: rgba(255, 255, 255, 0.1);
                      border: 1px solid rgba(255, 255, 255, 0.3);
                      border-radius: 0.375rem;
                      color: white;
                      transition: all 0.3s ease;
                      font-size: 0.875rem;
                    }
                    .modern-input.textarea {
                      height: auto;
                      padding: 0.5rem 0.75rem;
                    }
                    .modern-input::placeholder {
                      color: rgba(255, 255, 255, 0.7);
                    }
                    .modern-input:focus {
                      outline: none;
                      border-color: #60a5fa;
                      background: rgba(255, 255, 255, 0.2);
                      box-shadow: 0 0 0 2px rgba(96, 165, 250, 0.4);
                    }
                    .modern-input:disabled {
                      background: rgba(255, 255, 255, 0.05);
                      color: rgba(255, 255, 255, 0.5);
                      cursor: not-allowed;
                    }
                    .modern-select {
                      width: 100%;
                      padding: 0.5rem 2rem 0.5rem 0.75rem;
                      height: 2.25rem;
                      background: rgba(255, 255, 255, 0.1);
                      border: 1px solid rgba(255, 255, 255, 0.3);
                      border-radius: 0.375rem;
                      color: white;
                      transition: all 0.3s ease;
                      appearance: none;
                      background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23FFFFFF%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.4-12.8z%22%2F%3E%3C%2Fsvg%3E");
                      background-repeat: no-repeat;
                      background-position: right 0.5rem top 50%;
                      background-size: 0.65rem auto;
                      font-size: 0.875rem;
                    }
                    .modern-select option {
                      background: #1e293b;
                      color: white;
                    }
                    .modern-select:focus {
                      outline: none;
                      border-color: #60a5fa;
                      background-color: rgba(255, 255, 255, 0.2);
                      box-shadow: 0 0 0 2px rgba(96, 165, 250, 0.4);
                    }
                    .modern-select:disabled {
                      background-color: rgba(255, 255, 255, 0.05);
                      color: rgba(255, 255, 255, 0.5);
                      cursor: not-allowed;
                    }
                    .modern-btn {
                      background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
                      box-shadow: 0 4px 10px rgba(29, 78, 216, 0.3);
                      transition: all 0.3s ease;
                      font-weight: 500;
                    }
                    .modern-btn:hover:not(:disabled) {
                      transform: translateY(-1px);
                      box-shadow: 0 4px 12px rgba(29, 78, 216, 0.5);
                      background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
                    }
                    .modern-btn:active:not(:disabled) {
                      transform: translateY(1px);
                      box-shadow: 0 2px 8px rgba(29, 78, 216, 0.3);
                    }
                    .modern-error {
                      border-color: #ef4444 !important;
                      background-color: rgba(239, 68, 68, 0.1) !important;
                    }
                    .error-text {
                      color: #fca5a5;
                      font-size: 0.75rem;
                      margin-top: 0.125rem;
                      display: block;
                    }
                  `}
                </style>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <input
                      name="name"
                      value={form.name}
                      onChange={handleInputChange}
                      type="text"
                      placeholder="Full Name"
                      className={`modern-input ${submitAttempted && errors.name ? "modern-error" : ""}`}
                      readOnly={isCustomerLoggedIn}
                    />
                    {submitAttempted && errors.name && (
                      <span className="error-text">{errors.name}</span>
                    )}
                  </div>
                  <div>
                    <input
                      name="email"
                      value={form.email}
                      onChange={handleInputChange}
                      type="email"
                      placeholder="Email Address"
                      className={`modern-input ${submitAttempted && errors.email ? "modern-error" : ""}`}
                      readOnly={isCustomerLoggedIn}
                    />
                    {submitAttempted && errors.email && (
                      <span className="error-text">{errors.email}</span>
                    )}
                  </div>
                  <div>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleInputChange}
                      maxLength={10}
                      placeholder="Phone Number"
                      className={`modern-input ${submitAttempted && errors.phone ? "modern-error" : ""}`}
                      readOnly={isCustomerLoggedIn}
                    />
                    {submitAttempted && errors.phone && (
                      <span className="error-text">{errors.phone}</span>
                    )}
                  </div>
                  <div>
                    {isCustomerLoggedIn ? (
                      <input
                        type="text"
                        value={profile?.district || ""}
                        readOnly
                        className="modern-input"
                        disabled
                      />
                    ) : (
                      <select
                        name="district"
                        value={form.district}
                        onChange={handleInputChange}
                        className={`modern-select ${submitAttempted && errors.district ? "modern-error" : ""}`}
                        disabled={isLoadingDistricts || isSubmitting}
                      >
                        <option value="">
                          {isLoadingDistricts ? "Loading..." : "Select District"}
                        </option>
                        {districts?.map((district) => (
                          <option key={district} value={district}>
                            {district}
                          </option>
                        ))}
                      </select>
                    )}
                    {submitAttempted && errors.district && (
                      <span className="error-text">{errors.district}</span>
                    )}
                  </div>
                  <div>
                    {isCustomerLoggedIn ? (
                      <input
                        type="text"
                        value={profile?.taluk || ""}
                        readOnly
                        className="modern-input"
                        disabled
                      />
                    ) : (
                      <select
                        name="taluk"
                        value={form.taluk}
                        onChange={handleInputChange}
                        className={`modern-select ${submitAttempted && errors.taluk ? "modern-error" : ""}`}
                        disabled={!form.district || isLoadingTaluks || isSubmitting}
                      >
                        <option value="">
                          {isLoadingTaluks ? "Loading..." : "Select Taluk"}
                        </option>
                        {taluks?.map((taluk) => (
                          <option key={taluk} value={taluk}>
                            {taluk}
                          </option>
                        ))}
                      </select>
                    )}
                    {submitAttempted && errors.taluk && (
                      <span className="error-text">{errors.taluk}</span>
                    )}
                  </div>
                </div>
                <div>
                  <textarea
                    name="query_message"
                    value={form.query_message}
                    onChange={handleInputChange}
                    placeholder="How can we help you?"
                    rows="2"
                    className={`modern-input textarea resize-none ${submitAttempted && errors.query_message ? "modern-error" : ""}`}
                  />
                  {submitAttempted && errors.query_message && (
                    <span className="error-text">{errors.query_message}</span>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full text-white py-2 rounded-md flex items-center justify-center modern-btn disabled:opacity-70 disabled:cursor-not-allowed mt-1"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  {isSubmitting ? "Submitting..." : "Submit Request"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="container mx-auto px-4 py-14 bg-white">
        <div className="mb-12">
          <h2 className="text-xl md:text-2xl font-bold text-center text-blue-600 uppercase mb-8">Commercial Construction Services</h2>
          <div className={`space-y-8 ${showMore ? "" : "max-h-[60vh] overflow-hidden"}`}>
            <div>
              <h3 className="text-lg md:text-xl font-bold text-blue-600 mb-4">1. Luxury Residential Construction</h3>
              <ul className="list-disc pl-8 mb-6 text-sm md:text-base text-gray-600 leading-relaxed space-y-2">
                <li style={{ fontSize: "14px" }}><strong>Custom Homes:</strong> Tailored to your exact specifications, our custom homes are designed with high-end finishes and premium materials, featuring advanced technology like smart home systems that elevate convenience and comfort.</li>
                <li style={{ fontSize: "14px" }}><strong>High-End Apartments and Condominiums:</strong> We create luxurious apartments and condos with top-tier amenities, including fitness centers, swimming pools, panoramic views, and prime location advantages.</li>
                <li style={{ fontSize: "14px" }}><strong>Estate Homes:</strong> For those seeking ultimate luxury, we build expansive estate homes on large plots of land, complete with private pools, home theaters, gardens, and other opulent features that cater to your every need.</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg md:text-xl font-bold text-blue-600 mb-4">2. Commercial Premium Construction</h3>
              <ul className="list-disc pl-8 mb-6 text-sm md:text-base text-gray-600 leading-relaxed space-y-2">
                <li style={{ fontSize: "14px" }}><strong>Luxury Hotels and Resorts:</strong> We design five-star hotels and resorts that offer world-class services, including spas, gourmet restaurants, and luxury suites, all crafted with the finest materials and attention to detail.</li>
                <li style={{ fontSize: "14px" }}><strong>Corporate Headquarters:</strong> Our premium office buildings are designed to foster productivity and comfort, featuring high-end finishes, modern technology, and energy-efficient systems for a sustainable, state-of-the-art work environment.</li>
                <li style={{ fontSize: "14px" }}><strong>Retail Flagship Stores:</strong> For high-end brands, we create premium commercial spaces with exclusive architectural features and custom-designed interiors that reflect the brand's identity and cater to a luxury clientele.</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg md:text-xl font-bold text-blue-600 mb-4">3. Premium Green or Sustainable Construction</h3>
              <ul className="list-disc pl-8 mb-6 text-sm md:text-base text-gray-600 leading-relaxed space-y-2">
                <li style={{ fontSize: "14px" }}><strong>Eco-Luxury Homes:</strong> Ducktail embraces eco-friendly construction by building homes with sustainable materials and energy-efficient designs. Our homes include features such as solar panels, rainwater harvesting systems, and advanced insulation to minimize environmental impact while offering the luxury of modern living.</li>
                <li style={{ fontSize: "14px" }}><strong>LEED-Certified Buildings:</strong> We create green commercial buildings that meet LEED certification standards, utilizing energy-efficient construction methods and sustainable practices to deliver high-performance, environmentally friendly spaces.</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg md:text-xl font-bold text-blue-600 mb-4">4. Heritage and Restoration Projects</h3>
              <ul className="list-disc pl-8 mb-6 text-sm md:text-base text-gray-600 leading-relaxed space-y-2">
                <li style={{ fontSize: "14px" }}><strong>Historical Building Restorations:</strong> We specialize in preserving and restoring historic buildings, combining modern materials and techniques to maintain the aesthetic and architectural integrity of the original structure while bringing it up to contemporary standards.</li>
                <li style={{ fontSize: "14px" }}><strong>Architectural Renovations:</strong> Our team refurbishes classic buildings, blending historical elements with luxurious, modern upgrades, ensuring that the timeless beauty of the structure is preserved while providing enhanced functionality.</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg md:text-xl font-bold text-blue-600 mb-4">5. High-Tech Construction</h3>
              <ul className="list-disc pl-8 mb-6 text-sm md:text-base text-gray-600 leading-relaxed space-y-2">
                <li style={{ fontSize: "14px" }}><strong>Smart Homes:</strong> Ducktail designs homes equipped with the latest in automation technology, including AI-controlled lighting, security systems, climate control, and entertainment systems, offering the ultimate in convenience and modern living.</li>
                <li style={{ fontSize: "14px" }}><strong>Technology-Integrated Offices:</strong> We create workspaces that embrace the future with advanced technology, such as interactive whiteboards, automated lighting systems, and IT infrastructure to optimize productivity and business operations.</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg md:text-xl font-bold text-blue-600 mb-4">6. Premium Commercial and Industrial Buildings</h3>
              <ul className="list-disc pl-8 mb-6 text-sm md:text-base text-gray-600 leading-relaxed space-y-2">
                <li style={{ fontSize: "14px" }}><strong>Data Centres:</strong> Our data centers are state-of-the-art facilities designed to meet the demands of modern data storage and processing, incorporating high-end security systems, climate control, and reliable power backup to ensure optimal performance.</li>
                <li style={{ fontSize: "14px" }}><strong>Premium Warehousing:</strong> We offer high-end commercial and industrial spaces, including advanced systems for inventory management, automation, and energy-efficient features to streamline operations and ensure sustainability in warehousing.</li>
              </ul>
            </div>
          </div>
          {!showMore && (<div className="text-center mt-6"><button onClick={() => setShowMore(true)} className="text-orange-500 font-semibold">Show More</button></div>)}
          {showMore && (<div className="text-center mt-6"><button onClick={() => setShowMore(false)} className="text-orange-500 font-semibold">Show Less</button></div>)}
        </div>
      </div>
      <MobileBottomNav />
      <div className="md:pb-0">

      </div> </div>
  );
};

export default PremiumConstruction;